/**
 * pw-home-cta.js -- tasks.db #1381 / #1721 (2026-09-30): which home-page section, and which button
 * in it, do visitors click? Plan: docs/plans/1721-home-cta-instrumentation.md (sections 3 and 10).
 *
 * One delegated, capture-phase click listener on document. A click on an <a>/<button> inside an
 * element carrying data-cs-section sends ONE GA4 event, `home_cta_click`, ONLY through
 * window.PWAnalyticsGate.gtagEvent (tasks.db #1722), so staging / preview / Theme Editor clicks
 * never reach the live GA4 property. No gate on the page means nothing is sent.
 *
 * Params (event-scoped custom dimensions Paul registers in GA4, plan section 7):
 *   cs_section      data-cs-section of the enclosing home section
 *   cs_cta          data-cs-cta of the clicked element, or '(untagged)' (coverage-gap signal)
 *   cs_slot         data-cs-slot (1-based displayed position) or ''
 *   cs_section_pos  1-based position of that section among [data-cs-section] on the page
 *   cs_layout       section keys in DOM order joined by '>' (Theme Editor reorders show up)
 *   cs_device       viewport layout class at click time: mobile <750, tablet 750-989, desktop
 *                   >=990 (Horizon's breakpoints), '' if unknown. Independent of GA4's UA-based
 *                   Device category, which is the point (#1381's tablet outlier).
 *   link_url        the clicked href WITHOUT query/hash (GA4 built-in dimension); '' for a <button>
 *                   or a mailto:/tel: link
 *   transport_type  'beacon', so the hit survives the navigation the click starts
 *
 * Never calls preventDefault and never throws into the click: analytics must not block or break
 * navigation. Loaded only on the index template (layout/theme.liquid), after the gate.
 */
(function () {
  'use strict';
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__pwHomeCtaBound) return; // a double load must not double-count clicks
  window.__pwHomeCtaBound = true;

  var MAX = 100; // GA4 event-param value limit

  function cap(v) {
    var s = v == null ? '' : String(v);
    return s.length > MAX ? s.slice(0, MAX) : s;
  }

  // link_url: path only. A query string or hash could carry a pasted tracking/tokenized value, and
  // mailto:/tel: hrefs are contact details; none of that belongs in GA4 (3-model review, 2026-09-30).
  function safeUrl(href) {
    var h = href == null ? '' : String(href);
    if (/^\s*(mailto|tel|sms):/i.test(h)) return '';
    return h.split(/[?#]/)[0];
  }

  function deviceClass() {
    var w = window.innerWidth;
    if (typeof w !== 'number' || !(w > 0)) return '';
    if (w < 750) return 'mobile';
    if (w < 990) return 'tablet';
    return 'desktop';
  }

  function onClick(evt) {
    try {
      var gate = window.PWAnalyticsGate;
      if (!gate || typeof gate.gtagEvent !== 'function') return;
      var t = evt && evt.target;
      if (t && typeof t.closest !== 'function') t = t.parentElement; // text node
      if (!t || typeof t.closest !== 'function') return;
      var ctl = t.closest('a, button');
      if (!ctl) return;
      var section = ctl.closest('[data-cs-section]');
      if (!section) return;

      var key = section.getAttribute('data-cs-section') || '';
      var all = document.querySelectorAll('[data-cs-section]');
      var keys = [];
      var pos = 0;
      for (var i = 0; i < all.length; i++) {
        keys.push(all[i].getAttribute('data-cs-section') || '');
        if (all[i] === section) pos = i + 1;
      }

      window.PWAnalyticsGate.gtagEvent('home_cta_click', {
        cs_section: cap(key),
        cs_cta: cap(ctl.getAttribute('data-cs-cta') || '(untagged)'),
        cs_slot: cap(ctl.getAttribute('data-cs-slot') || ''),
        cs_section_pos: cap(pos ? String(pos) : ''),
        cs_layout: cap(keys.join('>')),
        link_url: cap(safeUrl(ctl.getAttribute('href'))),
        transport_type: 'beacon',
        cs_device: deviceClass(),
      });
    } catch (e) {
      // Swallowed on purpose: a click must never fail because of analytics. The gate already
      // reports a throwing gtag once; anything else here is a bug in this file, not the page.
    }
  }

  document.addEventListener('click', onClick, true);
})();
